// @ts-nocheck — composed component; types added when touched again later
import React from "react";
import { CHANNEL_LABELS, STAGE_LABELS, stageDisplay } from "../../lib/channels";
// Phase F — severity glyphs for the cycle chip. The chip's color already
// signals urgency, but color alone fails for color-blind users and for
// quick at-a-glance scanning of a long deal list. A leading SVG glyph
// (12px, currentColor) makes severity readable without hover.
import { CheckCircle2, AlertCircle, AlertTriangle, Info } from "../../icons";
const { useState, useEffect, useMemo, useRef } = React;

/* composed/DealsView.tsx — HubSpot deals list + popover detail card
   (Phase 4 sub-step 5). Extracted from src/legacy/app.tsx (was inline
   ~lines 774-1953, 1180 LOC).

   Two exports:
     - default <DealsView>  — the full Contratti section: filters,
       search, tabs, summary, 4-state Cycle sort, table.
     - named <DealPopover>  — hover/click detail card for one deal.
       Used internally by DealsView; exported in case future code
       (e.g. NotificationBell) wants to reuse it.

   Cross-refs resolved at render time via the window global:
     - InfoIcon, Tooltip, MultiSelectDropdown (legacy app.tsx publish block)
     - EmptyState, Segmented (legacy primitives.tsx)
     - MONTH_KEYS, MONTH_LABELS_*, computeForecast, etc. (lib/model)

   Pure render — no state mutations on shared state. Filter/sort/tab
   state lives in this component's own useState. */
function DealPopover({ payload, ownersMap, lang, fmtMoney, hubspotDealUrl, onClose, fmtDate, relativeAgo, subscriptionCycle }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!payload || !ref.current) return;
    const { anchorRect } = payload;
    const popW = ref.current.offsetWidth || 320;
    const popH = ref.current.offsetHeight || 320;
    const m = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Horizontal: prefer right of card, flip to left, fall back to centered
    let left = anchorRect.right + m;
    if (left + popW > vw - m) {
      left = anchorRect.left - popW - m;
      if (left < m) left = Math.max(m, Math.min(vw - popW - m, anchorRect.left));
    }
    // Vertical: align top with card, clamp to viewport
    let top = anchorRect.top;
    if (top + popH > vh - m) top = Math.max(m, vh - popH - m);
    if (top < m) top = m;
    setPos({ left, top });
  }, [payload]);

  useEffect(() => {
    if (!payload) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const onScroll = () => onClose();
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onClose);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onClose);
    };
  }, [payload, onClose]);

  if (!payload) return null;
  const d = payload.deal;
  const meta = CHANNEL_LABELS[d.cell] || { it: d.cell, en: d.cell, color: "var(--ink-3)" };
  const isLost = STAGE_LABELS[d.stage]?.isClosedLost;
  const ownerName = ownersMap?.[d.ownerId]?.name || "—";
  const showCompany = d.company && d.company !== d.name;

  return (
    <>
      <div className="dpop-backdrop" onClick={onClose} />
      <div ref={ref}
        className={`dpop ${isLost ? "dpop--lost" : ""}`}
        style={{
          left: pos?.left ?? -9999,
          top: pos?.top ?? -9999,
          "--dpop-color": meta.color,
        }}>
        <div className="dpop__head">
          <div className="dpop__title">
            <div className="dpop__name">{d.name}</div>
            {showCompany && <div className="dpop__company">{d.company}</div>}
          </div>
          <button className="dpop__close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="dpop__amount-row">
          <span className={`dpop__amount ${isLost ? "dpop__amount--lost" : ""}`}>
            {isLost ? "−" : ""}{fmtMoney(d.amount)}
          </span>
          <span className="dpop__chip" style={{borderColor: meta.color, color: meta.color}}>{meta[lang]}</span>
        </div>
        <div className="dpop__rows">
          <div className="dpop__row"><span className="dpop__lbl">Stage</span><span>{stageDisplay(d.stage)}</span></div>
          <div className="dpop__row"><span className="dpop__lbl">{lang === "it" ? "Chiusura" : "Close"}</span><span>{fmtDate(d.closeDate)}</span></div>
          <div className="dpop__row"><span className="dpop__lbl">Owner</span><span>{ownerName}</span></div>
          <div className="dpop__row"><span className="dpop__lbl">{lang === "it" ? "Paese" : "Country"}</span><span>{d.country || "—"}</span></div>
          <div className="dpop__row"><span className="dpop__lbl">{lang === "it" ? "Ultimo contatto" : "Last contact"}</span><span>{relativeAgo(d.lastContacted) || "—"}</span></div>
          <div className="dpop__row"><span className="dpop__lbl">{lang === "it" ? "Ciclo" : "Cycle"}</span><span>{(() => {
            const c = subscriptionCycle ? subscriptionCycle(d.closeDate) : null;
            return c ? c.label : "—";
          })()}</span></div>
        </div>
        <a href={hubspotDealUrl(d.id)} target="_blank" rel="noreferrer" className="dpop__hslink">
          ↗ {lang === "it" ? "Apri su HubSpot" : "Open in HubSpot"}
        </a>
      </div>
    </>
  );
}

function DealsView({ deals, owners, portalId, lang, todayMonthKey, fmtMoney, aes, dealsTimeframe, setDealsTimeframe, todayQtrIdx, QUARTERS }) {
  // Multi-select channels (Tutti = all by default). Empty set means "show all" same as Tutti.
  // Channels: array of currently selected channel cells. By default all channels are selected
  // ("Tutti" master toggle is on); empty array = nothing visible. Marco's spec.
  const [selectedChannels, setSelectedChannels] = useState(() => Object.keys(CHANNEL_LABELS));
  const [selectedOwners, setSelectedOwners]     = useState([]);     // multi-select: array of ownerIds; empty = all
  // Stage filter: defaults to Closed Won only. Empty array = "show all stages".
  // Marco's preset buttons let him quickly switch to Won+Lost or Won+Lost+Committed.
  const [selectedStages, setSelectedStages]     = useState(() =>
    Object.entries(STAGE_LABELS).filter(([_, m]) => m.isClosedWon).map(([id]) => id)
  );
  const [search, setSearch]                     = useState("");
  const [viewMode, setViewMode]                 = useState("list");  // "list" | "card"
  const [sortBy, setSortBy]                     = useState("date");
  const [sortDir, setSortDir]                   = useState("desc");
  const [pageSize, setPageSize]                 = useState(10);
  const [page, setPage]                         = useState(1);
  // Timeframe submenu state — mirrors the header timeframe dropdown so user can pick
  // which Month / Quarter / Custom-range to filter to. Without this, clicking "Mese" or
  // "Trim" set the mode but offered no way to choose WHICH month/quarter.
  const [tfMenuOpen, setTfMenuOpen]             = useState(null);
  // Click on a card opens a floating popover with full deal details. Cards stay folded
  // so kanban columns don't break their layout. {deal, anchorRect} or null when closed.
  const [popoverDeal, setPopoverDeal]           = useState(null);
  const openPopover = (deal, anchorEl) => {
    const rect = anchorEl.getBoundingClientRect();
    setPopoverDeal({ deal, anchorRect: rect });
  };
  const closePopover = () => setPopoverDeal(null);

  const dealList = Array.isArray(deals) ? deals : [];
  const ownersMap = owners || {};

  // List of owner ids actually present in the data, with their resolved names.
  const ownersPresent = useMemo(() => {
    const ids = new Set();
    for (const d of dealList) if (d.ownerId) ids.add(d.ownerId);
    return [...ids].map(id => ({ id, name: ownersMap[id]?.name || id })).sort((a, b) => a.name.localeCompare(b.name));
  }, [dealList, ownersMap]);

  // AE owner ids (from `state.aes`). Marco's "team" filter = Marco (GTM) + all AEs.
  const teamOwnerIds = useMemo(() => {
    const ids = new Set(["32360969"]); // Marco's GTM owner id
    for (const ae of (aes || [])) if (ae.hubspotOwnerId) ids.add(ae.hubspotOwnerId);
    return ids;
  }, [aes]);

  const stagesPresent = useMemo(() => {
    const set = new Set();
    for (const d of dealList) set.add(d.stage);
    return [...set];
  }, [dealList]);

  // Timeframe-driven month set (uses the same QUARTERS / monthKey logic as the header).
  const monthsInTimeframe = useMemo(() => {
    const tf = dealsTimeframe;
    if (!tf || tf.mode === "fy" || tf.mode === "ytd") return null; // null = all months
    if (tf.mode === "month") return new Set([tf.monthKey || todayMonthKey]);
    if (tf.mode === "qtr") {
      const q = (QUARTERS || [])[tf.qtrIdx ?? todayQtrIdx];
      return new Set(q?.keys || []);
    }
    if (tf.mode === "custom") {
      const all = window.MONTH_KEYS || [];
      const a = all.indexOf(tf.customStart || "jan");
      const b = all.indexOf(tf.customEnd || "dec");
      if (a < 0 || b < 0) return null;
      return new Set(all.slice(Math.min(a, b), Math.max(a, b) + 1));
    }
    return null;
  }, [dealsTimeframe, todayMonthKey, todayQtrIdx, QUARTERS]);

  // Normalize for fuzzy-ish search: lowercase, strip punctuation/whitespace.
  // "P.R.G. Group" → "prggroup"; "pr" → "pr"; substring → match.
  const normalize = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const filtered = useMemo(() => {
    const qNorm = normalize(search);
    return dealList.filter(d => {
      // Channel filter: deal must be in selectedChannels. Empty = nothing visible (Tutti off).
      if (!selectedChannels.includes(d.cell)) return false;
      if (monthsInTimeframe && !monthsInTimeframe.has(d.monthKey)) return false;
      // Owner — multi-select; empty = all
      if (selectedOwners.length > 0 && !selectedOwners.includes(d.ownerId)) return false;
      // Stage — multi-select; empty array = show all. Default at mount = only Closed Won.
      if (selectedStages.length > 0 && !selectedStages.includes(d.stage)) return false;
      // (stageMode === "all" → no stage filter)
      if (qNorm) {
        const hay = normalize(`${d.name || ""}${d.company || ""}${ownersMap[d.ownerId]?.name || ""}`);
        if (!hay.includes(qNorm)) return false;
      }
      return true;
    });
  }, [dealList, selectedChannels, monthsInTimeframe, selectedOwners, selectedStages, search, ownersMap]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    // Precompute cycle days when sorting by cycle — avoids re-running cycleStatus
    // (loop up to 240 iterations) for every comparison and any potential errors.
    let cycleCache = null;
    if (sortBy === "cycle") {
      cycleCache = new Map();
      for (const d of arr) {
        try {
          const cs = cycleStatus(d);
          cycleCache.set(d.id, cs?.totalDays ?? -1);
        } catch (e) {
          cycleCache.set(d.id, -1);
        }
      }
    }
    arr.sort((a, b) => {
      // Cycle column with bucket modes (≤30 days vs >30 days): handled
      // separately because the asc/desc inversion at the end of this fn would
      // break the bucket ordering. Returns the final comparator value directly.
      if (sortBy === "cycle" && (sortDir === "bucket-first-up" || sortDir === "bucket-first-down")) {
        try {
          const daysA = cycleCache.get(a.id) ?? -1;
          const daysB = cycleCache.get(b.id) ?? -1;
          const bucketA = daysA <= 30 ? 0 : 1; // 0 = first month, 1 = >1 month
          const bucketB = daysB <= 30 ? 0 : 1;
          if (bucketA !== bucketB) {
            // Different buckets → group first based on mode
            return sortDir === "bucket-first-up" ? bucketA - bucketB : bucketB - bucketA;
          }
          // Same bucket → desc by days (newer/higher day count first within each bucket;
          // for the first-month bucket this surfaces the customers closest to the refund
          // window edge first, which is the actionable urgency signal)
          return daysB - daysA;
        } catch (e) { return 0; }
      }
      let cmp = 0;
      try {
        if (sortBy === "date") cmp = (a.closeDate || "").localeCompare(b.closeDate || "");
        else if (sortBy === "amount") cmp = (a.amount || 0) - (b.amount || 0);
        else if (sortBy === "stage") cmp = stageDisplay(a.stage).localeCompare(stageDisplay(b.stage));
        else if (sortBy === "owner") cmp = (ownersMap[a.ownerId]?.name || "").localeCompare(ownersMap[b.ownerId]?.name || "");
        else if (sortBy === "customer") cmp = (a.name || "").localeCompare(b.name || "");
        else if (sortBy === "channel") cmp = (CHANNEL_LABELS[a.cell]?.[lang] || a.cell || "").localeCompare(CHANNEL_LABELS[b.cell]?.[lang] || b.cell || "");
        else if (sortBy === "country") cmp = (a.country || "").localeCompare(b.country || "");
        else if (sortBy === "last") cmp = (a.lastContacted || "").localeCompare(b.lastContacted || "");
        else if (sortBy === "cycle") cmp = (cycleCache.get(a.id) ?? -1) - (cycleCache.get(b.id) ?? -1);
        else if (sortBy === "nexttask") cmp = (a.nextActivity || "").localeCompare(b.nextActivity || "");
      } catch (e) { cmp = 0; }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortBy, sortDir, ownersMap, lang]);

  // Split positive (Won/Committed/Setup/Upsell) from churn (Closed Lost). In list view, churn
  // gets its own red-tinted section at the bottom with internal scroll. Pagination applies only
  // to the positive list — churn always shown as a single scrollable group regardless of page.
  const nonLostSorted = useMemo(() => sorted.filter(d => !STAGE_LABELS[d.stage]?.isClosedLost), [sorted]);
  const lostSorted = useMemo(() => sorted.filter(d => STAGE_LABELS[d.stage]?.isClosedLost), [sorted]);

  // No pagination — the table itself scrolls vertically (Marco's spec). pageDeals = full list.
  const pageDeals = nonLostSorted;

  // Reset page to 1 whenever filters change.
  useEffect(() => { setPage(1); }, [selectedChannels, selectedOwners, selectedStages, search, monthsInTimeframe, pageSize]);

  // Smarter summary: separate Won (actual MRR) / Lost (churn) / Committed (pipeline, not yet won)
  // / Setup (one-shot). Avoids misleading totals.
  const summary = useMemo(() => {
    let wonMrr = 0, lostMrr = 0, committedMrr = 0, setupOneShot = 0;
    let wonCount = 0, lostCount = 0, committedCount = 0, setupCount = 0;
    for (const d of sorted) {
      const meta = STAGE_LABELS[d.stage];
      if (d.cell === "setupUpsell") {
        setupOneShot += d.amount || 0; setupCount++;
      } else if (meta?.isClosedLost) {
        lostMrr += d.amount || 0; lostCount++;
      } else if (meta?.isCommitted) {
        committedMrr += d.amount || 0; committedCount++;
      } else if (meta?.isClosedWon) {
        wonMrr += d.amount || 0; wonCount++;
      }
    }
    return { wonMrr, lostMrr, committedMrr, setupOneShot, wonCount, lostCount, committedCount, setupCount, netMrr: wonMrr - lostMrr };
  }, [sorted]);
  // 4-state cycle for the Cycle column (per Marco's spec, 2026-05-11):
  //   desc → asc → bucket-first-up (≤30d on top) → bucket-first-down (>30d on top) → desc...
  // Other columns keep the original 2-state asc/desc flip.
  // Switching columns always resets sortDir to "desc" so the cycle states are
  // scoped to the Cycle column and don't leak into asc/desc-only columns.
  const CYCLE_SORT_NEXT = { desc: "asc", asc: "bucket-first-up", "bucket-first-up": "bucket-first-down", "bucket-first-down": "desc" };
  const toggleSort = (col) => {
    if (sortBy !== col) { setSortBy(col); setSortDir("desc"); return; }
    if (col === "cycle") setSortDir(CYCLE_SORT_NEXT[sortDir] || "desc");
    else setSortDir(sortDir === "asc" ? "desc" : "asc");
  };
  // Channel tab logic (Marco's spec):
  //   - "Tutti" all selected, click X → ISOLATE X (only X visible)
  //   - Subset already active, click Y → TOGGLE Y in/out (additive multi-select)
  //   - Click "Tutti" → restore all
  // First click is isolation, subsequent clicks behave like sticky checkboxes — until Tutti
  // resets. No need for Cmd/Ctrl modifier; the behavior naturally chains.
  const toggleChannel = (cell) => {
    setSelectedChannels(prev => {
      const allCurrentlySelected = allChannelKeys.every(c => prev.includes(c));
      let next;
      if (allCurrentlySelected) {
        // Coming from "Tutti" → isolate to this single channel
        next = [cell];
      } else {
        // Subset mode → toggle this channel in/out (additive)
        next = prev.includes(cell) ? prev.filter(c => c !== cell) : [...prev, cell];
      }
      if (next.length > 0) setSelectedStages([]);
      return next;
    });
  };
  // "Tutti" master toggle: if all channels currently selected → deselect all; otherwise → select all.
  const allChannelKeys = Object.keys(CHANNEL_LABELS);
  const allSelected = allChannelKeys.every(c => selectedChannels.includes(c));
  const toggleAllChannels = () => {
    setSelectedChannels(allSelected ? [] : allChannelKeys);
    setSelectedStages([]);
  };
  const fmtDate = (iso) => {
    const d = safeDate(iso);
    if (!d) return "—";
    return d.toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { day: "2-digit", month: "short", year: "2-digit" });
  };
  const relativeAgo = (iso) => {
    const d = safeDate(iso);
    if (!d) return null;
    const ms = Date.now() - d.getTime();
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    if (days < 0) {
      const inDays = -days;
      return inDays === 0 ? (lang === "it" ? "oggi" : "today") : (lang === "it" ? `tra ${inDays}g` : `in ${inDays}d`);
    }
    if (days === 0) return lang === "it" ? "oggi" : "today";
    if (days === 1) return lang === "it" ? "ieri" : "yesterday";
    if (days < 30) return lang === "it" ? `${days}g fa` : `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return lang === "it" ? `${months}m fa` : `${months}mo ago`;
    return lang === "it" ? `${Math.floor(months / 12)}a fa` : `${Math.floor(months / 12)}y ago`;
  };
  // Cycle status — month into the subscription + last-contact health score.
  // Returns rich object so UI can color the chip based on multiple signals:
  //   * dayColor: based on days-into-cycle (0-2 green, 15-20 amber, 21+ red) — visualizes
  //     where we are in the renewal cycle.
  //   * severity (M1 only — refund window): looks at last contact to flag at-risk customers.
  //     - "no-contact": never spoken to → RED (urgent, no onboarding)
  //     - "stale-late": contact >14d AND we're past day 15 of cycle → ORANGE (catch up needed)
  //     - "fresh": contact ≤5d → GREEN
  //     - "stale-mild": contact 7-10d → LIGHT ORANGE
  //     - "ok": none of the above → neutral
  //   * label: shorter — just the day-of-cycle number ("23g")
  const cycleStatus = (d) => {
    if (!d || !d.closeDate) return null;
    const start = new Date(d.closeDate);
    if (isNaN(start.getTime())) return null;
    const now = new Date();
    if (start.getTime() > now.getTime()) return null;
    // Total tenure since closeDate. Below 90 days display in days; from 90+ display in months.
    const totalDays = Math.floor((now - start) / (1000 * 60 * 60 * 24));
    const totalMonths = Math.floor(totalDays / 30.44);
    const monthIdx = totalMonths + 1; // 1 = first month (refund window)
    const label = totalDays < 90 ? `${totalDays}g` : `${totalMonths}m`;
    // Day-of-month gradient — kept around for tooltip context, not the chip color anymore.
    let dayColor = "neutral";
    if (totalDays <= 2) dayColor = "fresh";
    let severity = "ok";
    let daysSinceContact = null;
    if (d.lastContacted) {
      const lc = new Date(d.lastContacted);
      if (!isNaN(lc.getTime())) daysSinceContact = Math.floor((now - lc) / (1000 * 60 * 60 * 24));
    }
    // Severity (refund/onboarding window — first 30 days). Drives the chip color.
    if (totalDays <= 30) {
      if (daysSinceContact === null) severity = "no-contact";
      else if (totalDays >= 15 && daysSinceContact > 14) severity = "stale-late";
      else if (daysSinceContact <= 5) severity = "fresh";
      else if (daysSinceContact >= 7 && daysSinceContact <= 10) severity = "stale-mild";
    }
    return { monthIdx, totalDays, totalMonths, dayColor, severity, daysSinceContact, label };
  };
  // Combined "tone" used to drive chip + HubSpot button color in card view.
  // Highest-priority signal wins: severity over dayColor (contact health is more actionable
  // than just "we're late in the cycle" — but dayColor still kicks in if contact looks ok).
  const cycleTone = (cs) => {
    if (!cs) return "neutral";
    if (cs.severity === "no-contact") return "danger";
    if (cs.severity === "stale-late") return "warn";
    if (cs.severity === "stale-mild") return "warn-soft";
    if (cs.severity === "fresh") return "fresh";
    if (cs.dayColor === "danger") return "danger";
    if (cs.dayColor === "warn") return "warn";
    if (cs.dayColor === "fresh") return "fresh";
    return "neutral";
  };
  // Phase F — severity glyph by tone. The chip's color already conveys
  // urgency, but the icon makes it readable without relying on color:
  //   danger   → AlertCircle    (urgent, no/very-stale contact)
  //   warn     → AlertTriangle  (attention, mid-cycle stale)
  //   warn-soft→ Info           (informational, mild attention)
  //   fresh    → CheckCircle2   (ok, active customer)
  //   neutral  → null           (no glyph; tone class still styles the chip)
  // Rendered inline with the day-count label inside .deals-cycle.
  const cycleGlyph = (tone) => {
    const props = { size: 12, strokeWidth: 2, "aria-hidden": true, className: "deals-cycle__glyph" };
    if (tone === "danger")    return <AlertCircle {...props} />;
    if (tone === "warn")      return <AlertTriangle {...props} />;
    if (tone === "warn-soft") return <Info {...props} />;
    if (tone === "fresh")     return <CheckCircle2 {...props} />;
    return null;
  };
  const cycleTooltip = (cs, lang) => {
    if (!cs) return "";
    if (cs.severity === "no-contact") return lang === "it"
      ? "⚠ Nessun contatto registrato con questo cliente. Onboarding mancante: contattalo subito per evitare il refund di fine mese."
      : "⚠ No contact logged for this customer. Onboarding missing: reach out now before the refund window closes.";
    if (cs.severity === "stale-late") return lang === "it"
      ? `⚠ Ultimo contatto ${cs.daysSinceContact}g fa e siamo già a metà mese. Verifica con lui che vada tutto bene prima della fine del periodo soddisfatti-o-rimborsati.`
      : `⚠ Last contact ${cs.daysSinceContact}d ago and we're past mid-cycle. Catch up with the customer before the refund window expires.`;
    if (cs.severity === "stale-mild") return lang === "it"
      ? `Ultimo contatto ${cs.daysSinceContact}g fa — controlla che tutto vada bene.`
      : `Last contact ${cs.daysSinceContact}d ago — quick check-in recommended.`;
    if (cs.severity === "fresh") return lang === "it"
      ? `Ultimo contatto ${cs.daysSinceContact}g fa. Cliente attivo.`
      : `Last contact ${cs.daysSinceContact}d ago. Active customer.`;
    return lang === "it"
      ? `Cliente da ${cs.totalDays}g (M${cs.monthIdx}).`
      : `Customer for ${cs.totalDays}d (M${cs.monthIdx}).`;
  };
  // Backwards-compat: keep the old subscriptionCycle name calling the new helper
  const subscriptionCycle = (iso) => cycleStatus({ closeDate: iso });
  // Next-task status — used for the task badge tooltip when a deal has an open task.
  const nextTaskStatus = (d) => {
    if (!d || !d.nextActivity) return null;
    const due = new Date(d.nextActivity);
    if (isNaN(due.getTime())) return null;
    const now = new Date();
    const daysUntil = Math.floor((due - now) / (1000 * 60 * 60 * 24));
    let tone = "neutral";
    if (daysUntil < 0) tone = "danger";
    else if (daysUntil <= 2) tone = "warn";
    return { date: due, daysUntil, tone, label: daysUntil < 0 ? `−${-daysUntil}g` : daysUntil === 0 ? (lang === "it" ? "oggi" : "today") : `+${daysUntil}g` };
  };
  // Last-contact urgency tone — applied as an underline on Committed deals so Marco can spot
  // pipeline customers who've gone silent. Scale per Marco's spec:
  //   0-3d   = fresh (green)
  //   3-10d  = warn-soft (yellow)
  //   10-20d = warn (orange)
  //   20-30d = warn-strong (deep orange)
  //   30+    = danger (red) — basically dead pipeline, contact ASAP
  const lastContactUrgency = (d) => {
    if (!d || !d.lastContacted) return "danger"; // no contact at all = highest urgency
    const lc = new Date(d.lastContacted);
    if (isNaN(lc.getTime())) return null;
    const days = Math.floor((Date.now() - lc.getTime()) / (1000 * 60 * 60 * 24));
    if (days <= 3) return "fresh";
    if (days <= 10) return "warn-soft";
    if (days <= 20) return "warn";
    if (days <= 30) return "warn-strong";
    return "danger";
  };
  const lastContactTooltip = (d, lang) => {
    if (!d.lastContacted) return lang === "it"
      ? "Nessun contatto registrato — contattalo per capire dove è bloccato"
      : "No contact logged — reach out to unblock";
    const lc = new Date(d.lastContacted);
    const days = Math.floor((Date.now() - lc.getTime()) / (1000 * 60 * 60 * 24));
    if (days > 30) return lang === "it" ? `${days}g senza contatto. Pipeline a rischio — chiama subito.` : `${days}d no contact. Pipeline at risk — call now.`;
    if (days > 20) return lang === "it" ? `${days}g senza contatto. Catch-up urgente.` : `${days}d no contact. Urgent catch-up.`;
    if (days > 10) return lang === "it" ? `${days}g senza contatto. Verifica come va.` : `${days}d no contact. Check in.`;
    if (days > 3) return lang === "it" ? `${days}g fa. Tutto sotto controllo, rivedi al bisogno.` : `${days}d ago. Routine check-in.`;
    return lang === "it" ? `Contatto recente (${days}g).` : `Recent contact (${days}d).`;
  };
  // MRR/ARR badge for a deal — based on subscription_tier_term from HubSpot. Defaults to MRR
  // since fonio's standard is monthly. Returns { label, variant } where variant tunes the chip color.
  const billingBadge = (d) => {
    const term = (d.subscriptionTerm || "").toLowerCase();
    if (term.includes("year") || term.includes("annu")) return { label: "ARR", variant: "arr" };
    if (term.includes("quarter") || term.includes("trim")) return { label: "QRR", variant: "qrr" };
    return { label: "MRR", variant: "mrr" };
  };
  // Determine if cycle column should appear (only when CW deals are in the list).
  const hasCwInList = useMemo(() => sorted.some(d => STAGE_LABELS[d.stage]?.isClosedWon), [sorted]);
  // HubSpot deep-link to a deal record. Uses the portalId returned by the Worker; falls back
  // to a search URL if portalId isn't available yet (so links still go somewhere useful).
  const hubspotDealUrl = (id) => portalId
    ? `https://app.hubspot.com/contacts/${portalId}/record/0-3/${id}`
    : `https://app.hubspot.com/`;

  if (dealList.length === 0) {
    return (
      <EmptyState
        icon="📭"
        title={lang === "it" ? "Nessun contratto in cache" : "No contracts cached"}
        message={lang === "it"
          ? "Clicca ↻ Aggiorna in alto per scaricare i deals da HubSpot."
          : "Click ↻ Refresh at the top to pull deals from HubSpot."}
      />
    );
  }

  const channelKeys = Object.keys(CHANNEL_LABELS);
  const tfMode = dealsTimeframe?.mode || "fy";
  const TF_OPTIONS = [
    {k:"month", l: lang === "it" ? "Mese" : "Month"},
    {k:"qtr",   l: lang === "it" ? "Trim." : "Qtr"},
    {k:"ytd",   l: "YTD"},
    {k:"fy",    l: "FY"},
    {k:"custom",l: lang === "it" ? "Custom" : "Custom"},
  ];

  return (
    <div className="deals-view">

      {/* ROW 1 — LEFT: Owner + Stage + Search · RIGHT: Lista/Card + Timeframe (with submenus) */}
      <div className="deals-filter-row">
        <div className="deals-filter-row__left">
          <input type="search" className="deals-search"
            placeholder={lang === "it" ? "Cerca cliente, deal, owner…" : "Search customer, deal, owner…"}
            value={search} onChange={e => setSearch(e.target.value)} />
          <span className="deals-filters__label">Owner</span>
          <MultiSelectDropdown
            value={selectedOwners}
            onChange={setSelectedOwners}
            lang={lang}
            allLabel={lang === "it" ? "Tutti gli owner" : "All owners"}
            options={ownersPresent.map(o => ({id: o.id, label: o.name}))}
            presetGroups={[{
              id: "team",
              label: lang === "it" ? "Sales team (Marco + AE)" : "Sales team (Marco + AE)",
              ids: [...teamOwnerIds],
            }]}
          />
          <span className="deals-filters__label">Stage</span>
          <MultiSelectDropdown
            value={selectedStages}
            onChange={setSelectedStages}
            lang={lang}
            allLabel={lang === "it" ? "Tutti gli stage" : "All stages"}
            options={stagesPresent.map(s => ({id: s, label: stageDisplay(s)}))}
            presetGroups={[
              {
                id: "won_only",
                label: lang === "it" ? "Solo Closed Won (default)" : "Closed Won only (default)",
                ids: Object.entries(STAGE_LABELS).filter(([_, m]) => m.isClosedWon).map(([id]) => id),
              },
              {
                id: "won_lost",
                label: lang === "it" ? "Closed Won + Lost (churns)" : "Closed Won + Lost (churns)",
                ids: Object.entries(STAGE_LABELS).filter(([_, m]) => m.isClosedWon || m.isClosedLost).map(([id]) => id),
              },
              {
                id: "won_lost_committed",
                label: lang === "it" ? "Won + Lost + Committed" : "Won + Lost + Committed",
                ids: Object.entries(STAGE_LABELS).filter(([_, m]) => m.isClosedWon || m.isClosedLost || m.isCommitted).map(([id]) => id),
              },
            ]}
          />
        </div>
        <div className="deals-filter-row__right">
          <Segmented
            size="sm"
            ariaLabel={lang === "it" ? "Vista" : "View"}
            value={viewMode}
            options={[
              {id: "list", icon: "☰", label: lang === "it" ? "Lista" : "List", title: lang === "it" ? "Vista lista" : "List view"},
              {id: "card", icon: "▦", label: lang === "it" ? "Card" : "Cards", title: lang === "it" ? "Vista card" : "Card view"},
            ]}
            onChange={setViewMode}
          />
          {/* Timeframe — same Segmented primitive as the header. */}
          {(() => {
            const activeMonthLabel = (() => {
              const o = TF_OPTIONS.find(x => x.k === "month");
              if (tfMode !== "month") return o.l;
              return `${o.l} · ${window.MONTH_SHORT[(dealsTimeframe?.monthKey) || todayMonthKey]}`;
            })();
            const activeQtrLabel = (() => {
              const o = TF_OPTIONS.find(x => x.k === "qtr");
              if (tfMode !== "qtr") return o.l;
              const q = (QUARTERS || [])[dealsTimeframe?.qtrIdx ?? todayQtrIdx];
              return q ? `${o.l} · ${q.label}` : o.l;
            })();
            const activeCustomLabel = (() => {
              const o = TF_OPTIONS.find(x => x.k === "custom");
              if (tfMode !== "custom") return o.l;
              if (dealsTimeframe?.customStart && dealsTimeframe?.customEnd) {
                return `${window.MONTH_SHORT[dealsTimeframe.customStart]} → ${window.MONTH_SHORT[dealsTimeframe.customEnd]}`;
              }
              return o.l;
            })();
            const labelFor = (k) => {
              if (k === "month") return activeMonthLabel;
              if (k === "qtr")   return activeQtrLabel;
              if (k === "custom") return activeCustomLabel;
              return TF_OPTIONS.find(x => x.k === k)?.l;
            };
            const tfDealsOptions = TF_OPTIONS.map(o => ({
              id: o.k,
              label: labelFor(o.k),
              expandable: o.k === "month" || o.k === "qtr" || o.k === "custom",
            }));
            const onTfChange = (id) => {
              if (id === "month") setDealsTimeframe({mode: "month", monthKey: dealsTimeframe?.monthKey || todayMonthKey});
              else if (id === "qtr") setDealsTimeframe({mode: "qtr", qtrIdx: dealsTimeframe?.qtrIdx ?? todayQtrIdx});
              else if (id === "custom") setDealsTimeframe({mode: "custom", customStart: dealsTimeframe?.customStart || "jan", customEnd: dealsTimeframe?.customEnd || "dec"});
              else setDealsTimeframe({mode: id});
            };
            return (
              <Segmented
                size="sm"
                ariaLabel={lang === "it" ? "Periodo" : "Timeframe"}
                value={tfMode}
                options={tfDealsOptions}
                onChange={onTfChange}
                expandedId={tfMenuOpen}
                onToggleExpand={setTfMenuOpen}
                renderDropdown={(opt) => {
                  if (opt.id === "month") {
                    return (window.MONTH_KEYS || []).map(mk => (
                      <button key={mk}
                        className={`tf-menu__item ${(dealsTimeframe?.monthKey || todayMonthKey) === mk ? "on" : ""}`}
                        onClick={() => { setDealsTimeframe({mode: "month", monthKey: mk}); setTfMenuOpen(null); }}>
                        {(lang === "it" ? window.MONTH_LABELS_IT : window.MONTH_LABELS_EN)[mk]}
                        {mk === todayMonthKey && <span className="tf-menu__pill">now</span>}
                      </button>
                    ));
                  }
                  if (opt.id === "qtr") {
                    return (QUARTERS || []).map(q => (
                      <button key={q.idx}
                        className={`tf-menu__item ${(dealsTimeframe?.qtrIdx ?? todayQtrIdx) === q.idx ? "on" : ""}`}
                        onClick={() => { setDealsTimeframe({mode: "qtr", qtrIdx: q.idx}); setTfMenuOpen(null); }}>
                        <b>{q.label}</b> · {q.keys.map(k => window.MONTH_SHORT[k]).join(" / ")}
                        {q.idx === todayQtrIdx && <span className="tf-menu__pill">now</span>}
                      </button>
                    ));
                  }
                  if (opt.id === "custom") {
                    return (
                      <div className="tf-menu--custom">
                        <div className="tf-menu__row">
                          <label>{lang === "it" ? "Da" : "From"}</label>
                          <select value={dealsTimeframe?.customStart || "jan"} onChange={e => setDealsTimeframe({...dealsTimeframe, mode: "custom", customStart: e.target.value})}>
                            {(window.MONTH_KEYS || []).map(mk => (
                              <option key={mk} value={mk}>{(lang === "it" ? window.MONTH_LABELS_IT : window.MONTH_LABELS_EN)[mk]}</option>
                            ))}
                          </select>
                        </div>
                        <div className="tf-menu__row">
                          <label>{lang === "it" ? "A" : "To"}</label>
                          <select value={dealsTimeframe?.customEnd || "dec"} onChange={e => setDealsTimeframe({...dealsTimeframe, mode: "custom", customEnd: e.target.value})}>
                            {(window.MONTH_KEYS || []).map(mk => (
                              <option key={mk} value={mk}>{(lang === "it" ? window.MONTH_LABELS_IT : window.MONTH_LABELS_EN)[mk]}</option>
                            ))}
                          </select>
                        </div>
                        <button className="tf-menu__close" onClick={() => setTfMenuOpen(null)}>{lang === "it" ? "Applica" : "Apply"}</button>
                      </div>
                    );
                  }
                  return null;
                }}
              />
            );
          })()}
        </div>
      </div>

      {/* ROW 2 — LEFT: inline minimal summary · RIGHT: channel tabs (Tutti/GTM/Partner/...) */}
      <div className="deals-tabs-row">
        {/* Inline minimal summary — left-aligned, low-key */}
        <div className="deals-summary-inline">
          <span className="deals-summary-inline__item">
            <b>{sorted.length}</b> {lang === "it" ? "deal" : "deals"}
          </span>
          {summary.wonCount > 0 && (
            <span className="deals-summary-inline__item deals-summary-inline__item--won">
              +{fmtMoney(summary.wonMrr)} <span className="deals-summary-inline__hint">won</span>
            </span>
          )}
          {summary.lostCount > 0 && (
            <span className="deals-summary-inline__item deals-summary-inline__item--lost">
              −{fmtMoney(summary.lostMrr)} <span className="deals-summary-inline__hint">churn</span>
            </span>
          )}
          {(summary.wonCount > 0 || summary.lostCount > 0) && (
            <span className="deals-summary-inline__item deals-summary-inline__item--net">
              <b>{summary.netMrr >= 0 ? "+" : ""}{fmtMoney(summary.netMrr)}</b> <span className="deals-summary-inline__hint">net MRR</span>
            </span>
          )}
          {summary.committedCount > 0 && (
            <span className="deals-summary-inline__item deals-summary-inline__item--committed">
              {fmtMoney(summary.committedMrr)} <span className="deals-summary-inline__hint">
                pipeline · {summary.committedCount}
                <InfoIcon tip={lang === "it" ? "Pipeline forward — Committed in HubSpot, non ancora Closed Won." : "Forward pipeline — Committed in HubSpot, not yet Closed Won."} />
              </span>
            </span>
          )}
          {summary.setupCount > 0 && (
            <span className="deals-summary-inline__item deals-summary-inline__item--setup">
              +{fmtMoney(summary.setupOneShot)} <span className="deals-summary-inline__hint">
                setup
                <InfoIcon tip={lang === "it" ? "Setup è una tantum, non MRR ricorrente." : "Setup is one-shot, not recurring MRR."} />
              </span>
            </span>
          )}
        </div>
        {/* Channel tabs — right-aligned */}
        <div className="deals-tabs">
          <button
            className={`deals-tab deals-tab--all ${allSelected ? "on" : ""}`}
            onClick={toggleAllChannels}>
            <span className="deals-tab__name">{lang === "it" ? "Tutti" : "All"}</span>
            <span className="deals-tab__count">{dealList.length}</span>
          </button>
          {channelKeys.map(c => {
            const meta = CHANNEL_LABELS[c];
            const count = dealList.filter(d => d.cell === c).length;
            if (count === 0) return null;
            const active = selectedChannels.includes(c);
            return (
              <button key={c}
                className={`deals-tab ${active ? "on" : ""}`}
                style={active ? {background: meta.color, color: "white", borderColor: meta.color} : {borderColor: meta.color, color: meta.color}}
                onClick={() => toggleChannel(c)}
                title={lang === "it" ? `Click su Tutti attivo: solo ${meta[lang]}. Click successivi: aggiungi/togli` : `Click when All is on: only ${meta[lang]}. Subsequent clicks: add/remove`}>
                <span className="deals-tab__name">{meta[lang]}</span>
                <span className="deals-tab__count">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* List view */}
      {viewMode === "list" && (() => {
        // Arrow indicator: 2-state for most columns (▲/▼). Cycle column adds
        // 2 bucket states with explicit labels so the header tells the user
        // which grouping is active.
        const arrow = (col) => {
          if (sortBy !== col) return "";
          if (sortDir === "asc") return "▲";
          if (sortDir === "desc") return "▼";
          if (sortDir === "bucket-first-up") return "▼ <1m";   // first-month bucket on top
          if (sortDir === "bucket-first-down") return "▼ ≥1m"; // >1-month bucket on top
          return "";
        };
        const Th = ({ col, label, className }) => (
          <th className={`deals-th--sortable ${className || ""}`} onClick={() => toggleSort(col)}>
            {label} <span className="th-sort">{arrow(col)}</span>
          </th>
        );
        return (
        <>
        <div className="deals-table-wrap">
          <table className="deals-table">
            <colgroup>
              <col className="col-date" /><col className="col-customer" /><col className="col-channel" />
              <col className="col-owner" /><col className="col-country" /><col className="col-amount" />
              <col className="col-stage" /><col className="col-last" />
              {hasCwInList && <col className="col-cycle" />}
              <col className="col-hs" />
            </colgroup>
            <thead>
              <tr>
                <Th col="date" label={lang === "it" ? "Chiusura" : "Close"} />
                <Th col="customer" label={lang === "it" ? "Cliente" : "Customer"} />
                <Th col="channel" label={lang === "it" ? "Canale" : "Channel"} />
                <Th col="owner" label="Owner" />
                <Th col="country" label={lang === "it" ? "Paese" : "Country"} />
                <Th col="amount" className="num" label={lang === "it" ? "Importo" : "Amount"} />
                <Th col="stage" label="Stage" />
                <Th col="last" label={lang === "it" ? "Ultimo contatto" : "Last contact"} />
                {hasCwInList && <Th col="cycle" label={lang === "it" ? "Ciclo" : "Cycle"} />}
                <th>HubSpot</th>
              </tr>
            </thead>
            <tbody>
              {pageDeals.map(d => {
                const meta = CHANNEL_LABELS[d.cell] || { it: d.cell, en: d.cell, color: "var(--ink-3)" };
                const stageMeta = STAGE_LABELS[d.stage];
                const isLost = stageMeta?.isClosedLost;
                const ownerName = ownersMap[d.ownerId]?.name || "—";
                return (
                  <tr key={d.id} className={`deals-row ${isLost ? "deals-row--lost" : ""}`}>
                    <td>{fmtDate(d.closeDate)}</td>
                    <td className="deals-name" title={d.company ? `${d.name} · ${d.company}` : d.name}>
                      <div className="deals-name__main">{d.name}</div>
                      {d.company && d.company !== d.name && <div className="deals-name__sub">{d.company}</div>}
                    </td>
                    <td><span className="deals-channel" style={{borderColor: meta.color, color: meta.color}}>{meta[lang]}</span></td>
                    <td className="deals-owner">{ownerName}</td>
                    <td className="deals-country">{d.country || "—"}</td>
                    <td className={`num ${isLost ? "deals-amt--lost" : ""}`}>
                      <span className="deals-amount-cell">
                        <span className="deals-amount-val">{isLost ? "−" : ""}{fmtMoney(d.amount)}</span>
                        {(() => {
                          const bb = billingBadge(d);
                          return <span className={`billing-badge billing-badge--${bb.variant}`}>{bb.label}</span>;
                        })()}
                      </span>
                    </td>
                    <td className="deals-stage">{stageDisplay(d.stage)}</td>
                    <td className="deals-rel">{(() => {
                      // For Committed deals, render the last-contact cell with an underline color
                      // signaling urgency (0-3d green … 30+ red). For other deals, plain text.
                      // If a task is scheduled, append a small badge with hover tooltip.
                      const isCommitted = stageMeta?.isCommitted;
                      const dateText = relativeAgo(d.lastContacted) || "—";
                      const nt = nextTaskStatus(d);
                      const taskBadge = nt ? (
                        <Tooltip tip={`${lang === "it" ? "Task programmato" : "Task scheduled"} · ${fmtDate(nt.date)}${nt.tone === "danger" ? (lang === "it" ? " · in ritardo" : " · overdue") : ""}`}>
                          <span className={`task-badge task-badge--${nt.tone}`}>▤</span>
                        </Tooltip>
                      ) : null;
                      if (isCommitted) {
                        const urg = lastContactUrgency(d);
                        return (
                          <span className="deals-lastcontact-cell">
                            <Tooltip tip={lastContactTooltip(d, lang)}>
                              <span className={`deals-lastcontact deals-lastcontact--${urg}`}>{dateText}</span>
                            </Tooltip>
                            {taskBadge}
                          </span>
                        );
                      }
                      return (
                        <span className="deals-lastcontact-cell">
                          <span title={d.lastContacted || ""}>{dateText}</span>
                          {taskBadge}
                        </span>
                      );
                    })()}</td>
                    {hasCwInList && (
                      <td className="deals-rel">{(() => {
                        if (!stageMeta?.isClosedWon) return <span className="deals-rel-muted">—</span>;
                        const c = cycleStatus(d);
                        if (!c) return "—";
                        const tone = cycleTone(c);
                        return (
                          <Tooltip tip={cycleTooltip(c, lang)}>
                            <span className={`deals-cycle deals-cycle--${tone}`}>
                              {cycleGlyph(tone)}
                              <span className="deals-cycle__label">{c.label}</span>
                            </span>
                          </Tooltip>
                        );
                      })()}</td>
                    )}
                    <td>
                      {(() => {
                        // HubSpot button outline reflects urgency — same rule as the inline-expanded
                        // card view so List + Card stay visually consistent.
                        // - Closed Won: cycle severity (fresh/warn/danger by months since close)
                        // - Committed:  next-task tone (fresh/warn/danger by due date)
                        // - Closed Lost / other: neutral
                        const cs = stageMeta?.isClosedWon ? cycleStatus(d) : null;
                        const nt = stageMeta?.isCommitted ? nextTaskStatus(d) : null;
                        const tone = cs ? cycleTone(cs) : (nt ? nt.tone : "neutral");
                        return (
                          <a href={hubspotDealUrl(d.id)} target="_blank" rel="noreferrer"
                            className={`deals-hslink deals-hslink--${tone}`}
                            title={lang === "it" ? "Apri su HubSpot" : "Open in HubSpot"}
                            onClick={e => e.stopPropagation()}>↗</a>
                        );
                      })()}
                    </td>
                  </tr>
                );
              })}
              {pageDeals.length === 0 && lostSorted.length === 0 && (
                <tr><td colSpan={8 + (hasCwInList ? 1 : 0) + 1} className="deals-empty-row">
                  <EmptyState
                    size="compact"
                    icon="📭"
                    title={lang === "it" ? "Nessun risultato" : "No results"}
                    message={lang === "it" ? "Nessun deal corrisponde ai filtri attivi." : "No deals match the active filters."}
                  />
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Churn block — separate red-tinted scrollable section below the main table.
            Sits OUTSIDE deals-table-wrap so the main scroll and churn scroll are independent. */}
        {selectedChannels.includes("churnAbs") && lostSorted.length > 0 && (
            <div className="deals-churn-block">
              <div className="deals-churn-block__head">
                <span className="deals-churn-block__title">
                  {lang === "it" ? "Churn" : "Churn"}
                  <span className="deals-churn-block__count">{lostSorted.length}</span>
                </span>
                <span className="deals-churn-block__total">
                  −{fmtMoney(lostSorted.reduce((s, d) => s + (d.amount || 0), 0))}
                </span>
              </div>
              <div className="deals-churn-block__scroll">
                <table className="deals-table deals-table--churn">
                  <colgroup>
                    <col className="col-date" /><col className="col-customer" /><col className="col-channel" />
                    <col className="col-owner" /><col className="col-country" /><col className="col-amount" />
                    <col className="col-stage" /><col className="col-last" />
                    {hasCwInList && <col className="col-cycle" />}
                    <col className="col-hs" />
                  </colgroup>
                  <tbody>
                    {lostSorted.map(d => {
                      const meta = CHANNEL_LABELS[d.cell] || { it: d.cell, en: d.cell, color: "var(--ink-3)" };
                      const ownerName = ownersMap[d.ownerId]?.name || "—";
                      return (
                        <tr key={d.id} className="deals-row deals-row--lost">
                          <td>{fmtDate(d.closeDate)}</td>
                          <td className="deals-name" title={d.company ? `${d.name} · ${d.company}` : d.name}>
                            <div className="deals-name__main">{d.name}</div>
                            {d.company && d.company !== d.name && <div className="deals-name__sub">{d.company}</div>}
                          </td>
                          <td><span className="deals-channel" style={{borderColor: meta.color, color: meta.color}}>{meta[lang]}</span></td>
                          <td className="deals-owner">{ownerName}</td>
                          <td className="deals-country">{d.country || "—"}</td>
                          <td className="num deals-amt--lost">
                            <span className="deals-amount-cell">
                              <span className="deals-amount-val">−{fmtMoney(d.amount)}</span>
                              {(() => {
                                const bb = billingBadge(d);
                                return <span className={`billing-badge billing-badge--${bb.variant}`}>{bb.label}</span>;
                              })()}
                            </span>
                          </td>
                          <td className="deals-stage">{stageDisplay(d.stage)}</td>
                          <td className="deals-rel">{relativeAgo(d.lastContacted) || "—"}</td>
                          {hasCwInList && <td className="deals-rel"><span className="deals-rel-muted">—</span></td>}
                          <td>
                            <a href={hubspotDealUrl(d.id)} target="_blank" rel="noreferrer"
                              className="deals-hslink" title={lang === "it" ? "Apri su HubSpot" : "Open in HubSpot"}>↗</a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
        );
      })()}

      {/* Card view — Kanban layout grouped by month. Behaviour adapts to the timeframe mode:
          - FY: 6×2 grid of months (existing default)
          - YTD: same grid but only past+current months
          - QTR: 3 wide+tall cells (1 per month in the quarter); cards inline-expanded
          - Month: 1 huge cell taking the full surface; cards inline-expanded
          For QTR/Month modes the cell header gains a Won/Committed/Churn breakdown line
          and cards render in their expanded form (no click-to-popover needed). */}
      {viewMode === "card" && (() => {
        let visibleMonths = monthsInTimeframe ? [...monthsInTimeframe] : (window.MONTH_KEYS || []);
        // YTD: clip future months — only show months up to today's month inclusive.
        if (tfMode === "ytd") {
          const todayIdx = (window.MONTH_KEYS || []).indexOf(todayMonthKey);
          visibleMonths = (window.MONTH_KEYS || []).slice(0, todayIdx + 1);
        }
        const orderedMonths = (window.MONTH_KEYS || []).filter(mk => visibleMonths.includes(mk));
        const groups = {};
        for (const mk of orderedMonths) groups[mk] = [];
        for (const d of sorted) if (groups[d.monthKey]) groups[d.monthKey].push(d);
        if (orderedMonths.length === 0 || sorted.length === 0) {
          return <EmptyState
            size="compact"
            icon="📭"
            title={lang === "it" ? "Nessun risultato" : "No results"}
            message={lang === "it" ? "Nessun deal corrisponde ai filtri attivi." : "No deals match the active filters."}
          />;
        }
        // Layout class drives grid columns + cell height + card-expanded mode
        const isInlineExpanded = tfMode === "month" || tfMode === "qtr";
        const layoutClass = tfMode === "month" ? "deals-kanban--month"
          : tfMode === "qtr" ? "deals-kanban--qtr"
          : `deals-kanban--cols-${Math.min(6, orderedMonths.length)}`;
        return (
          <div className={`deals-kanban ${layoutClass}`}>
            {orderedMonths.map(mk => {
              const monthDeals = groups[mk];
              const monthLabel = (lang === "it" ? window.MONTH_LABELS_IT : window.MONTH_LABELS_EN)[mk];
              const monthTotal = monthDeals.reduce((s, d) => {
                const isLost = STAGE_LABELS[d.stage]?.isClosedLost;
                return s + (isLost ? -d.amount : d.amount);
              }, 0);
              const isCurrent = mk === todayMonthKey;
              // Per-cell breakdown for QTR/Month views (Won / Committed / Churn separately)
              let cellWonMrr = 0, cellLostMrr = 0, cellCommittedMrr = 0, cellSetup = 0;
              for (const d of monthDeals) {
                const meta = STAGE_LABELS[d.stage];
                if (d.cell === "setupUpsell") cellSetup += d.amount || 0;
                else if (meta?.isClosedLost) cellLostMrr += d.amount || 0;
                else if (meta?.isCommitted) cellCommittedMrr += d.amount || 0;
                else if (meta?.isClosedWon) cellWonMrr += d.amount || 0;
              }
              return (
                <div key={mk} className={`kanban-col ${isCurrent ? "kanban-col--now" : ""}`}>
                  <div className="kanban-col__head">
                    {tfMode !== "month" ? (
                      <button
                        className="kanban-col__name kanban-col__name--clickable"
                        onClick={() => setDealsTimeframe({ mode: "month", monthKey: mk })}
                        title={lang === "it" ? `Apri ${monthLabel} in vista Mese` : `Open ${monthLabel} in Month view`}>
                        {monthLabel}{isCurrent && <span className="kanban-col__pill">now</span>}
                      </button>
                    ) : (
                      <span className="kanban-col__name">{monthLabel}{isCurrent && <span className="kanban-col__pill">now</span>}</span>
                    )}
                    <span className="kanban-col__count">{monthDeals.length}</span>
                    <span className="kanban-col__total">{monthTotal >= 0 ? "+" : ""}{fmtMoney(monthTotal)}</span>
                  </div>
                  {isInlineExpanded && (cellWonMrr || cellCommittedMrr || cellLostMrr || cellSetup) > 0 && (
                    <div className="kanban-col__breakdown">
                      {cellWonMrr > 0 && <span className="kanban-col__bd kanban-col__bd--won">+{fmtMoney(cellWonMrr)} <em>won</em></span>}
                      {cellCommittedMrr > 0 && <span className="kanban-col__bd kanban-col__bd--committed">{fmtMoney(cellCommittedMrr)} <em>pipeline</em></span>}
                      {cellLostMrr > 0 && <span className="kanban-col__bd kanban-col__bd--lost">−{fmtMoney(cellLostMrr)} <em>churn</em></span>}
                      {cellSetup > 0 && <span className="kanban-col__bd kanban-col__bd--setup">+{fmtMoney(cellSetup)} <em>setup</em></span>}
                    </div>
                  )}
                  {(() => {
                    // Single scrollable list, Won/Committed first then Lost. Lost group gets a
                    // subtle inline label "Churn (N)" as separator — no rigid 2/3 split, no
                    // background tint, just typographic distinction.
                    const wonDeals = monthDeals.filter(d => !STAGE_LABELS[d.stage]?.isClosedLost);
                    const lostDeals = monthDeals.filter(d => STAGE_LABELS[d.stage]?.isClosedLost);
                    if (monthDeals.length === 0) {
                      return <div className="kanban-col__empty">—</div>;
                    }
                    const renderCard = (d) => {
                      const meta = CHANNEL_LABELS[d.cell] || { it: d.cell, en: d.cell, color: "var(--ink-3)" };
                      const stageMeta = STAGE_LABELS[d.stage];
                      const isLost = stageMeta?.isClosedLost;
                      const ownerName = ownersMap[d.ownerId]?.name || "—";
                      const ownerShort = ownerName.split(" ")[0] || "—";
                      const isActive = popoverDeal?.deal.id === d.id;
                      // QTR/Month mode → render the card in EXPANDED form inline (no click-to-popover).
                      if (isInlineExpanded) {
                        return (
                          <div key={d.id}
                            className={`fcard fcard--inline-expanded ${isLost ? "fcard--lost" : ""}`}
                            style={{"--fcard-color": meta.color}}>
                            <div className="fcard__head">
                              <div className="fcard__title">
                                <span className="fcard__name" title={d.name}>{d.name}</span>
                                {d.company && d.company !== d.name && <span className="fcard__company">{d.company}</span>}
                              </div>
                              {(() => {
                                // HubSpot button color follows the cycle severity for CW deals,
                                // or the next-task tone for Committed deals — visual signal of urgency.
                                const cs = stageMeta?.isClosedWon ? cycleStatus(d) : null;
                                const nt = stageMeta?.isCommitted ? nextTaskStatus(d) : null;
                                const tone = cs ? cycleTone(cs) : (nt ? nt.tone : "neutral");
                                return (
                                  <a href={hubspotDealUrl(d.id)} target="_blank" rel="noreferrer"
                                    className={`fcard__hslink fcard__hslink--${tone}`}
                                    title={lang === "it" ? "Apri su HubSpot" : "Open in HubSpot"}>↗</a>
                                );
                              })()}
                            </div>
                            <div className="fcard__amount-row">
                              <span className={`fcard__amount-big ${isLost ? "fcard__amount--lost" : ""}`}>
                                {isLost ? "−" : ""}{fmtMoney(d.amount)}
                                {(() => {
                                  const bb = billingBadge(d);
                                  return <span className={`billing-badge billing-badge--${bb.variant}`}>{bb.label}</span>;
                                })()}
                              </span>
                              <span className="fcard__chip" style={{borderColor: meta.color, color: meta.color}}>{meta[lang]}</span>
                            </div>
                            <div className="fcard__rows">
                              <div className="fcard__row"><span className="fcard__lbl">Stage</span><span>{stageDisplay(d.stage)}</span></div>
                              <div className="fcard__row"><span className="fcard__lbl">{lang === "it" ? "Chiusura" : "Close"}</span><span>{fmtDate(d.closeDate)}</span></div>
                              <div className="fcard__row"><span className="fcard__lbl">Owner</span><span>{ownerName}</span></div>
                              <div className="fcard__row"><span className="fcard__lbl">{lang === "it" ? "Paese" : "Country"}</span><span>{d.country || "—"}</span></div>
                              {(() => {
                                const isCommitted = stageMeta?.isCommitted;
                                const dateText = relativeAgo(d.lastContacted) || "—";
                                const nt = nextTaskStatus(d);
                                const taskBadge = nt ? (
                                  <Tooltip tip={`${lang === "it" ? "Task programmato" : "Task scheduled"} · ${fmtDate(nt.date)}${nt.tone === "danger" ? (lang === "it" ? " · in ritardo" : " · overdue") : ""}`}>
                                    <span className={`task-badge task-badge--${nt.tone}`}>▤</span>
                                  </Tooltip>
                                ) : null;
                                return (
                                  <div className="fcard__row">
                                    <span className="fcard__lbl">{lang === "it" ? "Ultimo contatto" : "Last contact"}</span>
                                    <span className="deals-lastcontact-cell">
                                      {isCommitted
                                        ? <Tooltip tip={lastContactTooltip(d, lang)}>
                                            <span className={`deals-lastcontact deals-lastcontact--${lastContactUrgency(d)}`}>{dateText}</span>
                                          </Tooltip>
                                        : <span>{dateText}</span>}
                                      {taskBadge}
                                    </span>
                                  </div>
                                );
                              })()}
                              {stageMeta?.isClosedWon && (() => {
                                const c = cycleStatus(d);
                                if (!c) return null;
                                const tone = cycleTone(c);
                                return (
                                  <div className="fcard__row"><span className="fcard__lbl">{lang === "it" ? "Ciclo" : "Cycle"}</span>
                                    <Tooltip tip={cycleTooltip(c, lang)}>
                                      <span className={`deals-cycle deals-cycle--${tone}`}>
                                        {cycleGlyph(tone)}
                                        <span className="deals-cycle__label">{c.label}</span>
                                      </span>
                                    </Tooltip>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        );
                      }
                      // Default folded card (FY / YTD). Apply same urgency-driven HubSpot
                      // button tone + billing badge as the list view so palette stays
                      // consistent across views.
                      const cs = stageMeta?.isClosedWon ? cycleStatus(d) : null;
                      const nt = stageMeta?.isCommitted ? nextTaskStatus(d) : null;
                      const tone = cs ? cycleTone(cs) : (nt ? nt.tone : "neutral");
                      const bb = billingBadge(d);
                      return (
                        <div key={d.id}
                          className={`fcard ${isLost ? "fcard--lost" : ""} ${isActive ? "fcard--active" : ""}`}
                          style={{"--fcard-color": meta.color}}>
                          <div className="fcard__head"
                            onClick={(e) => isActive ? closePopover() : openPopover(d, e.currentTarget)}>
                            <div className="fcard__title">
                              <span className="fcard__name" title={d.name}>{d.name}</span>
                            </div>
                            <div className="fcard__meta">
                              <span className={`fcard__amount ${isLost ? "fcard__amount--lost" : ""}`}>
                                {isLost ? "−" : ""}{fmtMoney(d.amount)}
                                <span className={`billing-badge billing-badge--${bb.variant}`}>{bb.label}</span>
                              </span>
                              <span className="fcard__owner" title={ownerName}>{ownerShort}</span>
                              <a href={hubspotDealUrl(d.id)} target="_blank" rel="noreferrer"
                                className={`fcard__hslink fcard__hslink--${tone}`}
                                onClick={e => e.stopPropagation()}
                                title={lang === "it" ? "Apri su HubSpot" : "Open in HubSpot"}>↗</a>
                            </div>
                          </div>
                        </div>
                      );
                    };
                    return (
                      <>
                        <div className="kanban-col__body kanban-col__body--won">
                          {wonDeals.map(renderCard)}
                        </div>
                        {lostDeals.length > 0 && (
                          <>
                            <div className="kanban-col__sep">
                              <span className="kanban-col__sep-label">churn · {lostDeals.length}</span>
                            </div>
                            <div className="kanban-col__body kanban-col__body--lost">
                              {lostDeals.map(renderCard)}
                            </div>
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Color legend — chip tones (Cycle) + underline urgency scale (Last contact, Committed). */}
      {viewMode === "list" && sorted.length > 0 && (
        <div className="deals-legend">
          <span className="deals-legend__lbl">{lang === "it" ? "Last contact (Committed)" : "Last contact (Committed)"}:</span>
          <span className="deals-legend__item"><span className="deals-lastcontact deals-lastcontact--fresh">0-3g</span></span>
          <span className="deals-legend__item"><span className="deals-lastcontact deals-lastcontact--warn-soft">3-10g</span></span>
          <span className="deals-legend__item"><span className="deals-lastcontact deals-lastcontact--warn">10-20g</span></span>
          <span className="deals-legend__item"><span className="deals-lastcontact deals-lastcontact--warn-strong">20-30g</span></span>
          <span className="deals-legend__item"><span className="deals-lastcontact deals-lastcontact--danger">30+ / mai</span></span>
          {hasCwInList && <>
            <span className="deals-legend__sep">·</span>
            <span className="deals-legend__lbl">{lang === "it" ? "Ciclo (CW)" : "Cycle (CW)"}:</span>
            <span className="deals-legend__item"><span className="deals-cycle deals-cycle--fresh">{cycleGlyph("fresh")}</span> {lang === "it" ? "ok" : "ok"}</span>
            <span className="deals-legend__item"><span className="deals-cycle deals-cycle--warn">{cycleGlyph("warn")}</span> {lang === "it" ? "attenzione" : "warning"}</span>
            <span className="deals-legend__item"><span className="deals-cycle deals-cycle--danger">{cycleGlyph("danger")}</span> {lang === "it" ? "urgente" : "urgent"}</span>
          </>}
          <span className="deals-legend__sep">·</span>
          <span className="deals-legend__item"><span className="task-badge task-badge--neutral">▤</span> {lang === "it" ? "task programmato (hover)" : "task scheduled (hover)"}</span>
        </div>
      )}
      {/* No pagination footer — the main table and the churn block both scroll on their own. */}
      {/* Floating popover with full deal details — opens on card click */}
      <DealPopover
        payload={popoverDeal}
        ownersMap={ownersMap}
        lang={lang}
        fmtMoney={fmtMoney}
        hubspotDealUrl={hubspotDealUrl}
        onClose={closePopover}
        fmtDate={fmtDate}
        relativeAgo={relativeAgo}
        subscriptionCycle={subscriptionCycle}
      />
    </div>
  );
}

// MixBar + MonthDetailCard moved to forecast-table.jsx (Phase B sub-PR 3)

// === Single-month focused chart: horizontal bar comparison Target vs Real per channel ===

export default DealsView;
export { DealPopover };
